/**
 * CountryRiskBadge.js
 * --------------------
 * Reusable flag + risk-category pill for nationality fields.
 *
 * Usage:
 *   <CountryRiskBadge code="PK" />
 *   <CountryRiskBadge code={customer.customer_data?.nationality} compact />
 *
 * Renders an emoji flag + ISO code + category label coloured by severity.
 * Falls back to a neutral pill for standard (low-risk) countries.
 */
import React from "react";

/** Convert ISO-3166 alpha-2 → flag emoji (regional-indicator letters). */
export function flagEmoji(code) {
  if (!code || typeof code !== "string" || code.length !== 2) return "";
  const upper = code.toUpperCase();
  const A = 0x1F1E6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(A + upper.charCodeAt(0) - a, A + upper.charCodeAt(1) - a);
}

const CATEGORY_STYLE = {
  FATF_BLACKLIST:      { bg: "rgba(220,38,38,0.18)",  border: "#dc262688", color: "#ef4444", label: "FATF Blacklist" },
  OFAC_COMPREHENSIVE:  { bg: "rgba(220,38,38,0.18)",  border: "#dc262688", color: "#ef4444", label: "OFAC Sanctioned" },
  FATF_GREYLIST:       { bg: "rgba(245,158,11,0.15)", border: "#f59e0b66", color: "#f59e0b", label: "FATF Greylist" },
  HIGH_RISK_NON_FATF:  { bg: "rgba(168,85,247,0.15)", border: "#a855f766", color: "#c084fc", label: "High Risk (non-FATF)" },
  LOW:                 { bg: "rgba(148,163,184,0.10)", border: "#94a3b855", color: "#94a3b8", label: "Standard" },
};

/**
 * @param {string} code - ISO-3166 alpha-2 country code.
 * @param {object} [geo] - Optional pre-fetched geo result from /api/country-risk/lookup.
 *   If omitted, the badge fetches on mount (one quick GET, cached).
 * @param {boolean} [compact=false] - Hide the textual category label, keep flag + code.
 */
export function CountryRiskBadge({ code, geo, compact = false }) {
  const [resolved, setResolved] = React.useState(geo || null);

  React.useEffect(() => {
    if (geo || !code) return;
    let cancelled = false;
    fetch(`${window.location.origin}/api/country-risk/lookup?code=${encodeURIComponent(code)}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setResolved(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [code, geo]);

  if (!code) return null;

  const category = resolved?.geo?.category || "LOW";
  const style = CATEGORY_STYLE[category] || CATEGORY_STYLE.LOW;
  const fullLabel = resolved?.geo?.label || style.label;

  return (
    <span
      data-testid={`country-risk-badge-${code}`}
      title={`${code} — ${fullLabel}${resolved ? ` · Score ${resolved.geo.score}/100` : ""}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: compact ? "2px 7px" : "3px 9px",
        background: style.bg, border: `1px solid ${style.border}`,
        borderRadius: 6, color: style.color,
        fontSize: 11, fontWeight: 600, lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: 13 }}>{flagEmoji(code) || "🌐"}</span>
      <span style={{ fontFamily: "ui-monospace, monospace" }}>{code.toUpperCase()}</span>
      {!compact && category !== "LOW" && (
        <span style={{ marginLeft: 4, opacity: 0.9 }}>— {style.label}</span>
      )}
    </span>
  );
}

export default CountryRiskBadge;
